import { contactDetails, updateContactDetails } from 'services/contact';
import { updateRelation } from 'services/relationServices';
import { getUserDetails, updateTeamMember, updateUserDetails } from 'services/teamManagement.service';
import { listCasesAndLeads, getCaseDetails, addTeamMember, updateCaseContact } from 'services/CaseServices';
import { viewLeadDetailsModified } from 'services/lead/leadServices';
import { teamMemberRolesList } from 'constants/automationConstants';
import {
  generateAttendeesStatusOptions,
  getFormattedAttendees,
} from 'modules/settings/workflow_automation/submodules/events/helpers/createEventHelpers';
import { getAccessibleList } from 'modules/settings/workflow_automation/helpers/automationCommonHelpers';

import { updateWorkFlowAutomationRules } from 'services/workflowAutomation/workflowAutomationRuleServices';
import {
  generateAssigneeDetailsObjectArray,
  generatePayloadCompatibleFormattedAssigneesList,
} from 'modules/settings/workflow_automation/submodules/tasks/helpers/addTaskHelpers';
import {
  generateMessageRecipientsDetailsObjectArray,
  generatePayloadCompatibleFormattedMessageRecipientsList,
  getMessageSenderDetails,
} from 'modules/settings/workflow_automation/submodules/messages/helpers/createMessageHelpers';
import moment from 'moment';

const getCasesAndLeads = async (setCasesList, setLeadsList) => {
  try {
    let response = await listCasesAndLeads();

    if (response?.data) {
      const { lead_list = [], case_list = [] } = response?.data || {};

      const getFormattedList = (arr = []) => {
        let formattedList = [];
        if (arr?.length) {
          formattedList = arr?.map((item, key) => {
            return {
              label: item?.case_name_number,
              value: item?.case_id,
            };
          });
        }

        return formattedList;
      };

      setCasesList(getFormattedList(case_list));
      setLeadsList(getFormattedList(lead_list));
    }
  } catch (error) {
    console.log('error', error);
  }
};

const getBillingContactsList = async (props) => {
  try {
    const { tenantId, tenantSk, generateFullName, setBillingContactsOptions, rowData } = props || {};
    const { pk, sk, is_lead, case_id } = rowData || {};

    let response;

    if (!is_lead) {
      let timeEntryData = {};
      response = await getCaseDetails(tenantId, tenantSk, pk, sk, timeEntryData, case_id);

      if (response?.data) {
        const { client_details = [], relations = [] } = response?.data || {};

        let primaryContact = client_details?.length ? client_details[0] : {};
        let relationContacts = relations?.length ? relations : [];
        let billingContacts = [{ ...(primaryContact || {}), is_client: true }, ...relationContacts];
        let formattedBillingContacts = [];

        if (billingContacts?.length) {
          billingContacts?.forEach((contact, idx) => {
            let contactObj = {};
            let label = generateFullName(contact);

            if (contact?.is_primary_contact) {
              label = `${label} (Primary)`;
            } else {
              label = `${label} (Relation)`;
            }

            contactObj = {
              label,
              value: contact,
            };
            formattedBillingContacts.push(contactObj);
          });
        }

        setBillingContactsOptions(formattedBillingContacts);
      }
    }
  } catch (error) {
    console.log('error', error);
  }
};

const getContactDetails = async (props) => {
  try {
    const { setContactData, rowData } = props || {};
    const { pk, sk, contact_id } = rowData || {};
    let response = await contactDetails(sk, pk, contact_id);
    if (response.data) {
      setContactData(response.data);
    }
  } catch (error) {
    console.log('error', error);
  }
};

const getTeamMembersList = async (props) => {
  const { tenantId, tenantSk, rowData, generateFullName, setTeamMembersList, firmUsersArr, error_code } = props || {};
  const { pk, sk, case_id } = rowData || {};
  let response;

  try {
    let timeEntryData = {};
    let existingTeamMembersWithoutRoles = [];
    let existingTeamMembersWithRoles = [];

    //Removing user-groups from firmUserList
    // let firmUsersListFiltered = firmUsersArr?.filter((item, idx) => !(item?.res_type === 'user-group' || item?.user_group_id));
    let firmUsersListFiltered = firmUsersArr?.filter((item, idx) =>
      ['GROUP_MEMBERS_ARCHIVED', 'GROUP_MEMBERS_UNCONFIRMED']?.includes(error_code)
        ? item?.status === 'ACTIVE' && item?.is_confirmed === true && !(item?.res_type === 'user-group' || item?.user_group_id)
        : !(item?.res_type === 'user-group' || item?.user_group_id)
    );

    //Calling view-case-details API to get team members of the case with error-log
    response = await getCaseDetails(tenantId, tenantSk, pk, sk, timeEntryData, case_id);

    //Removing already existing team-members with lead-roles associated to selected case.
    if (response?.data?.team_member?.length > 0) {
      response?.data?.team_member?.forEach((item, idx) => {
        let condition =
          item?.case_id && (item?.is_lead_associate || item?.is_lead_attorney || item?.is_lead_case_manager || item?.is_lead_paralegal);

        if (condition) {
          existingTeamMembersWithRoles?.push(item);
        } else {
          existingTeamMembersWithoutRoles?.push(item);
        }
      });

      //Removing existing team members (view-case-details API) from firmUsersListFiltered
      firmUsersListFiltered?.forEach((fusr, key) => {
        response?.data?.team_member?.forEach((tmbr, idx) => {
          let condition = fusr?.firm_user_id === tmbr?.firm_user_id || fusr?.client_cognito_username === tmbr?.client_cognito_username;
          if (condition) {
            firmUsersListFiltered?.splice(key, 1);
          }
        });
      });
    }

    let allAavailableFirmUsers = [...existingTeamMembersWithoutRoles, ...firmUsersListFiltered]?.map((item) => ({
      ...item,
      label: generateFullName(item),
    }));
    setTeamMembersList({
      existingTeamMembersWithRoles,
      availableTeamMembersWithoutRoles: allAavailableFirmUsers,
    });
  } catch (error) {
    console.log('error', error);
  }
};

//========================== Error fix handling functions starts =======================================

//USER_NOT_IN_CASE
const handleUserNotInCaseError = async (params = {}) => {
  let {
    sk,
    pk,
    client_cognito_username,
    firm_user_id,
    case_id,
    is_lead,
    onErrorFixSuccess = () => {},
    onErrorFixFail = () => {},
    errorPk,
    errorSk,
  } = params || {};

  try {
    let oldTeamMembersOfLeads = null;

    if (is_lead) {
      let data = { sk: case_id };

      let leadResponse = await viewLeadDetailsModified(data);

      if (leadResponse?.data) {
        let leadAssignTo = leadResponse?.data?.leads?.lead_assign_to;

        if (leadAssignTo?.length) oldTeamMembersOfLeads = leadAssignTo[0];
      }
    }

    sk = '';
    pk = '';

    let responseOne = await getUserDetails(sk, pk, firm_user_id, client_cognito_username);

    if (responseOne?.data) {
      let link_to_user = '';
      let time_entry_data = {};
      let data = {
        team_member: [responseOne?.data],
        error_pk: errorPk,
        error_sk: errorSk,
      };

      if (is_lead) data['old_team_member'] = oldTeamMembersOfLeads;

      let responseTwo = await addTeamMember(data, link_to_user, case_id, is_lead, time_entry_data);

      if (responseTwo?.data) {
        onErrorFixSuccess();
      }
    }
  } catch (error) {
    let errMsg = error?.response?.data;

    onErrorFixFail(errMsg);
    console.log('error', error);
  }
};

//NO_EMAIL, NO_LAWFT_ACCESS, NO_PHONE_NUMBER
const handleUpdateErrorOccuredContactDetails = async (params = {}) => {
  const {
    sk,
    pk,
    contact_id,
    is_lead,
    error_code: errorCode,
    automationErrorLogCodes: { noLawftAccess, noPhoneNumber, noEmail },
    contactData,
    onErrorFixSuccess = () => {},
    onErrorFixFail = () => {},
    getValues = () => {},
    errorPk,
    errorSk,
  } = params || {};
  try {
    let responseOne;

    if (errorCode !== noLawftAccess) {
      responseOne = await contactDetails(sk, pk, contact_id);
    }

    if (responseOne?.data || contactData) {
      let data = responseOne?.data || contactData || {};

      let email = getValues('email') || '';
      let lawftAccess = getValues('lawftAccess') || false;
      let phoneNumber = getValues('phone') || '';

      if (errorCode === noEmail) {
        data['email'] = email;
        data['is_create_client_portal'] = true;
      } else if (errorCode === noLawftAccess) {
        data['email'] = email;
        data['is_create_client_portal'] = lawftAccess;
      } else if (errorCode === noPhoneNumber) {
        data['phone'] = phoneNumber;
      }

      data['error_pk'] = errorPk;
      data['error_sk'] = errorSk;

      let responseTwo = await updateContactDetails(data, is_lead);
      if (responseTwo?.data) {
        onErrorFixSuccess();
      }
    }
  } catch (error) {
    let errMsg = error?.response?.data;

    onErrorFixFail(errMsg);

    console.log('error', error);
  }
};

//NO_BILLING_CONTACT
const handleNoBillingContactError = async (params = {}) => {
  const {
    contact_id = '',
    is_lead = false,
    case_id = '',
    billingContacts = [],
    onErrorFixSuccess = () => {},
    onErrorFixFail = () => {},
    errorPk,
    errorSk,
  } = params || {};

  try {
    if (billingContacts?.length) {
      let primaryContact = [];
      let relationContacts = [];
      let responseOne;
      let responseTwo;
      let isApiSuccess = false;

      primaryContact = billingContacts?.filter((contact, idx) => contact?.is_primary_contact);
      relationContacts = billingContacts?.filter((contact, idx) => !contact?.is_primary_contact);

      if (primaryContact?.length > 0) {
        let contactUpdate = true;
        let data = primaryContact[0];
        data.is_billing_contact = true;
        data['error_pk'] = errorPk;
        data['error_sk'] = errorSk;
        responseOne = await updateCaseContact(case_id, contact_id, data, contactUpdate, is_lead);

        if (responseOne?.data) isApiSuccess = true;
      }

      if (relationContacts?.length > 0) {
        isApiSuccess = false;
        let relationContactsModified = relationContacts?.map((rel, id) => {
          return {
            ...rel,
            is_billing_contact: true,
          };
        });
        let data = {
          relations: relationContactsModified,
          error_pk: errorPk,
          error_sk: errorSk,
        };
        let timeEntryData = {};
        responseTwo = await updateRelation(data, is_lead, timeEntryData);

        if (responseTwo?.data) isApiSuccess = true;
      }

      if (isApiSuccess) {
        onErrorFixSuccess();
      }
    }
  } catch (error) {
    let errMsg = error?.response?.data;

    onErrorFixFail(errMsg);
    console.log('error', error);
  }
};

//NO_LEAD_ATTORNEY, NO_LEAD_ASSOCIATE, NO_LEAD_PARALEGAL, NO_LEAD_CASE_MANAGER
const handleNoLeadTeamMemberError = async (params = {}) => {
  const {
    tenantId,
    tenantSk,
    pk,
    sk,
    is_lead,
    case_id,
    error_code,
    selectedTeamMember,
    teamMembersList,
    automationErrorLogCodes: { noLeadAttorney, noLeadAssociate, noLeadParalegal, noLeadCaseManager },
    onErrorFixSuccess,
    onErrorFixFail,
    errorPk,
    errorSk,
  } = params;

  const getTeamMemberRoleStatus = () => {
    let apiKeysObj = {
      is_lead_attorney: false,
      is_lead_associate: false,
      is_lead_paralegal: false,
      is_lead_case_manager: false,
    };

    switch (error_code) {
      case noLeadAttorney:
        apiKeysObj['is_lead_attorney'] = true;
        break;

      case noLeadAssociate:
        apiKeysObj['is_lead_associate'] = true;
        break;

      case noLeadParalegal:
        apiKeysObj['is_lead_paralegal'] = true;
        break;

      case noLeadCaseManager:
        apiKeysObj['is_lead_case_manager'] = true;
        break;

      default:
        break;
    }

    return apiKeysObj;
  };

  try {
    if (selectedTeamMember) {
      //If the selected team member is not an existing team member of the case, then we need to remove sk and pk of this user since we are gonna add this user to this case from the error-log section. So the sk and pk string value will be changed in the BE side
      if (!selectedTeamMember?.case_id) {
        delete selectedTeamMember['sk'];
        delete selectedTeamMember['pk'];
        selectedTeamMember['case_id'] = case_id;
      }

      //we need to use update-team-member API and not add-team-member API
      let caseDetails = await getCaseDetails(tenantId, tenantSk, pk, sk, case_id);

      const existingTeamMembers = caseDetails?.data?.team_member || [];
      const updatedTeamMembers = existingTeamMembers.map((member) =>
        member.client_cognito_username === selectedTeamMember.client_cognito_username
          ? { ...member, ...getTeamMemberRoleStatus() } // Update role for existing member
          : member
      );

      const isMemberExists = existingTeamMembers.some(
        (member) => member.client_cognito_username === selectedTeamMember.client_cognito_username
      );

      if (!isMemberExists) {
        updatedTeamMembers.push({
          ...selectedTeamMember,
          ...getTeamMemberRoleStatus(),
        });
      }

      let data = {
        team_member: updatedTeamMembers,
        error_pk: errorPk,
        error_sk: errorSk,
      };

      let response = await updateTeamMember(data, is_lead, case_id);

      if (response?.data) onErrorFixSuccess();
    }
  } catch (error) {
    let errMsg = error?.response?.data;
    onErrorFixFail(errMsg);
    console.log('error', error);
  }
};

//USER_ARCHIVED

const handleUsersAutomationErrors = async (functionParams) => {
  const { errorPk, errorSk } = functionParams || {};

  if (functionParams?.selectedAutomationErrorLogDetails?.action_code === 'ACT_EVENT_CREATE') {
    try {
      // Extract the array of action unique identifiers from the execution sequence
      const actionUniqueIdentifiers = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence?.map(
        (action) => action.action_unique_identifier
      );

      // Check if the action_unique_identifier exists in the extracted array
      const isIdentifierMatched = actionUniqueIdentifiers?.includes(
        functionParams?.selectedAutomationErrorLogDetails?.action_unique_identifier
      );

      // Declare matchedExecution and matchedIndex outside of the if statement
      let matchedExecution;
      let matchedIndex = -1; // Default value if no match is found

      if (isIdentifierMatched) {
        // Find the matching execution details and index
        matchedIndex = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence.findIndex(
          (action) => action.action_unique_identifier === functionParams?.selectedAutomationErrorLogDetails?.action_unique_identifier
        );

        // Get the matched execution using the found index
        matchedExecution = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence[matchedIndex];

        // Log the details of the matched execution and its index

        // Implement the mapping and filtering logic based on the provided conditions
        const newSelectedFirmUsers = matchedExecution?.action_description?.body?.selectedAttendeesStatusOptions
          ?.map((option) => {
            if (option.res_type === 'user') {
              // Check if the user is confirmed and active
              if (option.is_confirmed === true && option.status === 'ACTIVE') {
                return option.client_cognito_username;
              }
            } else if (option.res_type === 'user-group') {
              // Check if the group status is active
              if (option?.status === 'ACTIVE' || option?.status === undefined) {
                return option.group_id;
              }
            } else {
              // If res_type is not present, take the value directly
              return option.value;
            }
            return null; // Return null if none of the conditions match
          })
          .filter(Boolean); // Filter out any null or undefined values

        // // Combine newSelectedFirmUsers with the existing selectedFirmUsers
        // functionParams.selectedFirmUsers = [
        //   ...functionParams.selectedFirmUsers, // Existing selected users
        //   ...newSelectedFirmUsers, // Newly filtered users
        // ];

        // Combine newSelectedFirmUsers with the existing selectedFirmUsers
        functionParams.selectedFirmUsers = [
          ...functionParams?.selectedFirmUsers, // Existing selected users
          ...newSelectedFirmUsers, // Newly filtered users
        ];

        // Remove duplicates if necessary
        functionParams.selectedFirmUsers = Array.from(new Set(functionParams.selectedFirmUsers));

        // Continue with the remaining logic

        let accessibleList = [];
        let guests = [];
        let contactAssignees = [];
        let teamMemberRoles = [];
        let userGroupsList = [];
        let obj = {};
        let request_object = {};

        const newGroup = {
          groupLabel: 'Team Member Roles',
          code: 'TeamMemberRoles',
          items: [...teamMemberRolesList],
        };

        // Insert the new group at the second position
        const updatedOptions = [
          functionParams?.fullAttendeesList[0], // First existing group
          newGroup, // Your new group
          ...functionParams?.fullAttendeesList.slice(1), // Remaining existing groups
        ];

        // Create a set to track unique group labels
        const uniqueGroupLabels = new Set();

        // Filter out duplicate groups based on groupLabel
        const uniqueUpdatedOptions = updatedOptions?.filter((option) => {
          if (!uniqueGroupLabels?.has(option?.groupLabel)) {
            uniqueGroupLabels?.add(option?.groupLabel);
            return true;
          }
          return false;
        });

        let attendeesStatusValues = generateAttendeesStatusOptions({
          fullAttendeesList: uniqueUpdatedOptions,
          attendeesWatched: functionParams?.selectedFirmUsers,
        });

        guests = getFormattedAttendees({
          attendeesArr: attendeesStatusValues,
          isFirmUser: true,
          isBillingOrPrimary: false,
          isUserGroup: false,
        });

        // Filtering primary and billing only contacts
        contactAssignees = getFormattedAttendees({
          attendeesArr: attendeesStatusValues,
          isBillingOrPrimary: true,
          isFirmUser: false,
          isUserGroup: false,
          isTeamMember: false,
        });

        // Filtering user-groups
        userGroupsList = getFormattedAttendees({
          attendeesArr: attendeesStatusValues,
          isUserGroup: true,
          isBillingOrPrimary: false,
          isFirmUser: false,
          isTeamMember: false,
        });

        // Filtering team member roles
        teamMemberRoles = getFormattedAttendees({
          attendeesArr: attendeesStatusValues,
          isTeamMember: true,
          isUserGroup: false,
          isBillingOrPrimary: false,
          isFirmUser: false,
        });

        accessibleList = getAccessibleList(guests);

        const ruleValidationCheckPayload = {
          validation_trigger_code: functionParams?.selectedAutomationRuleDetails?.validation_trigger_code,
          validation_action_code: functionParams?.selectedAutomationRuleDetails?.validation_action_code,
        };

        request_object = {
          ...obj,
          ...{
            title: matchedExecution?.action_description?.body?.title,
            description: matchedExecution?.action_description?.body?.description,
            conferencing_url: matchedExecution?.action_description?.body?.conferencing_url,

            meeting_start_time: matchedExecution?.action_description?.body?.meeting_start_time ?? '',
            meeting_end_time: matchedExecution?.action_description?.body?.meeting_end_time ?? '',
            meeting_cycle: matchedExecution?.action_description?.body?.meeting_cycle || 0,

            meeting_due_date_count: matchedExecution?.action_description?.body?.meeting_due_date_count,
            meeting_due_date_period: matchedExecution?.action_description?.body?.meeting_due_date_period,

            guests: guests,
            contact_assignees: contactAssignees,
            firm_user_assignees: teamMemberRoles,
            groups: userGroupsList,
            accessible_list: accessibleList,
            selectedAttendees: functionParams?.selectedFirmUsers,
            selectedAttendeesStatusOptions: attendeesStatusValues,

            location: matchedExecution?.action_description?.body?.location,
            reminder: matchedExecution?.action_description?.body?.reminder,
            event_type: matchedExecution?.action_description?.body?.event_type,

            category: matchedExecution?.action_description?.body?.category,
            sync_with: matchedExecution?.action_description?.body?.sync_with,
            case_name: matchedExecution?.action_description?.body?.case_name,
            icon: matchedExecution?.action_description?.body?.icon,
            attachments: matchedExecution?.action_description?.body?.attachments,
            not_linked_to_case: false,
            track_action_time: matchedExecution?.action_description?.body?.track_action_time,
            manual_total_hours: matchedExecution?.action_description?.body?.manual_total_hours,
            action_unique_identifier: matchedExecution?.action_unique_identifie,
          },
        };

        // Update the matched execution in the selectedAutomationRuleDetails
        functionParams.selectedAutomationRuleDetails.workflow.execution_sequence[matchedIndex] = {
          ...matchedExecution,
          action_description: {
            ...matchedExecution.action_description,
            body: request_object, // Update with the new request_object
          },
        };

        try {
          let dataToBeSent = {
            ...ruleValidationCheckPayload,
            automation_rule_id: functionParams?.selectedAutomationRuleDetails?.automation_rule_id,
            is_deleted: functionParams?.selectedAutomationRuleDetails?.is_deleted,
            is_error: functionParams?.selectedAutomationRuleDetails?.is_error,
            res_type: functionParams?.selectedAutomationRuleDetails?.res_type,
            searchables: functionParams?.selectedAutomationRuleDetails?.searchables,
            tenant_id: functionParams?.selectedAutomationRuleDetails?.tenant_id,
            time: moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + '000+00:00',
            type_tenant: functionParams?.selectedAutomationRuleDetails?.tenant_id,
            user_id: functionParams?.selectedAutomationRuleDetails?.user_id,
            workflow_title: functionParams?.selectedAutomationRuleDetails?.workflow_title,
            automation_status: functionParams?.selectedAutomationRuleDetails?.automation_status?.toLocaleUpperCase(),
            is_lead_automation: functionParams?.selectedAutomationErrorLogDetails?.is_lead,
            // validation_trigger_code: triggerName?.ruleValidationCode,
            // validation_action_code: generateAutomationRuleValidationActionCodesList(globalAutomationData),
            workflow_description: functionParams?.selectedAutomationRuleDetails?.workflow_description,
            workflow: {
              fire_on: {
                trigger_code: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.trigger_code,
                module: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.module,
                label: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.label,
              },
              terminate_on: {},
              // filter: filter?.length > 0 && filter[0]?.filterBody?.label ? filter[0]?.filterBody : {},
              filter: functionParams?.selectedAutomationRuleDetails?.filter || [],
              action_period_expression: functionParams?.selectedAutomationRuleDetails?.workflow?.action_period_expression,
              execution_sequence: [...functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence],
            },
            practice_area: functionParams?.selectedAutomationRuleDetails?.practice_area,
          };
          dataToBeSent = {
            ...dataToBeSent,
            pk: functionParams?.selectedAutomationRuleDetails?.pk,
            sk: functionParams?.selectedAutomationRuleDetails?.sk,
            error_pk: errorPk,
            error_sk: errorSk,
          };

          let response = await updateWorkFlowAutomationRules(true, dataToBeSent);

          if (response) {
            functionParams?.onErrorFixSuccess();
          }
        } catch (error) {
          let errMsg = error?.response?.data;
          functionParams?.onErrorFixFail(errMsg);
          console.log('error', error);
        }
      }
    } catch (error) {
      console.log('error', error);
    }
  } else if (functionParams?.selectedAutomationErrorLogDetails?.action_code === 'ACT_TASK_CREATE') {
    try {
      // Extract the array of action unique identifiers from the execution sequence
      const actionUniqueIdentifiers = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence?.map(
        (action) => action.action_unique_identifier
      );

      // Check if the action_unique_identifier exists in the extracted array
      const isIdentifierMatched = actionUniqueIdentifiers?.includes(
        functionParams?.selectedAutomationErrorLogDetails?.action_unique_identifier
      );

      // Declare matchedExecution and matchedIndex outside of the if statement
      let matchedExecution;
      let matchedIndex = -1; // Default value if no match is found

      if (isIdentifierMatched) {
        // Find the matching execution details and index
        matchedIndex = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence.findIndex(
          (action) => action.action_unique_identifier === functionParams?.selectedAutomationErrorLogDetails?.action_unique_identifier
        );

        // Get the matched execution using the found index
        matchedExecution = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence[matchedIndex];

        // Log the details of the matched execution and its index

        // Implement the mapping and filtering logic based on the provided conditions
        const newSelectedFirmUsers = matchedExecution?.action_description?.body?.selectedAssigneeDetailsObjectArr
          ?.map((option) => {
            if (option?.res_type === 'user') {
              // Check if the user is confirmed and active
              if (option?.is_confirmed === true && option?.status === 'ACTIVE') {
                return option.client_cognito_username;
              }
            } else if (option?.res_type === 'user-group') {
              // Check if the group status is active
              if (option?.status === 'ACTIVE' || option?.status === undefined) {
                return option.group_id;
              }
            } else {
              // If res_type is not present, take the value directly
              return option.value;
            }
            return null; // Return null if none of the conditions match
          })
          .filter(Boolean); // Filter out any null or undefined values

        // Combine newSelectedFirmUsers with the existing selectedFirmUsers
        functionParams.selectedFirmUsers = [
          ...functionParams.selectedFirmUsers, // Existing selected users
          ...newSelectedFirmUsers, // Newly filtered users
        ];

        // Combine newSelectedFirmUsers with the existing selectedFirmUsers
        functionParams.selectedFirmUsers = [
          ...functionParams.selectedFirmUsers, // Existing selected users
          ...newSelectedFirmUsers, // Newly filtered users
        ];

        // Remove duplicates if necessary
        functionParams.selectedFirmUsers = Array.from(new Set(functionParams.selectedFirmUsers));

        // Continue with the remaining logic

        let firmUserAssignees = [];
        let teamMemberRoles = [];
        let contactAssignees = [];
        let userGroupsList = [];
        let accessibleList = [];
        let _reminder = [];
        let _subTask = [];
        let payloadCompatibleReminder = [];
        let obj = {};
        let request_object = {};

        const newGroup = {
          groupLabel: 'Team Member Roles',
          code: 'TeamMemberRoles',
          items: [...teamMemberRolesList],
        };

        // Insert the new group at the second position
        const updatedOptions = [
          functionParams?.fullAttendeesList[0], // First existing group
          newGroup, // Your new group
          ...functionParams?.fullAttendeesList.slice(1), // Remaining existing groups
        ];

        // Create a set to track unique group labels
        const uniqueGroupLabels = new Set();

        // Filter out duplicate groups based on groupLabel
        const uniqueUpdatedOptions = updatedOptions?.filter((option) => {
          if (!uniqueGroupLabels?.has(option?.groupLabel)) {
            uniqueGroupLabels?.add(option?.groupLabel);
            return true;
          }
          return false;
        });

        let assigneeDetailsObjectArr = generateAssigneeDetailsObjectArray({
          assignToList: uniqueUpdatedOptions,
          assignToWatched: functionParams?.selectedFirmUsers,
        });

        //filtering all assingnees other than billing and primary
        firmUserAssignees = generatePayloadCompatibleFormattedAssigneesList({
          assigneesArr: assigneeDetailsObjectArr,
          isFirmUser: true,
          isBillingOrPrimary: false,
          isUserGroup: false,
        });

        //filtering primary and billing only contacts
        contactAssignees = generatePayloadCompatibleFormattedAssigneesList({
          assigneesArr: assigneeDetailsObjectArr,
          isBillingOrPrimary: true,
          isFirmUser: false,
          isUserGroup: false,
          isTeamMember: false,
        });

        //filtering ponly user-groups list
        userGroupsList = generatePayloadCompatibleFormattedAssigneesList({
          assigneesArr: assigneeDetailsObjectArr,
          isUserGroup: true,
          isBillingOrPrimary: false,
          isFirmUser: false,
          isTeamMember: false,
        });
        // filtering team member roles
        teamMemberRoles = generatePayloadCompatibleFormattedAssigneesList({
          assigneesArr: assigneeDetailsObjectArr,
          isTeamMember: true,
          isUserGroup: false,
          isBillingOrPrimary: false,
          isFirmUser: false,
        });

        accessibleList = getAccessibleList(firmUserAssignees);

        const ruleValidationCheckPayload = {
          validation_trigger_code: functionParams?.selectedAutomationRuleDetails?.validation_trigger_code,
          validation_action_code: functionParams?.selectedAutomationRuleDetails?.validation_action_code,
        };

        request_object = {
          ...obj,
          ...{
            task_name: matchedExecution?.action_description?.body?.task_name,

            assign_to: firmUserAssignees,
            contact_assignees: contactAssignees,
            firm_user_assignees: teamMemberRoles,
            groups: userGroupsList,
            accessible_list: accessibleList,

            priority: matchedExecution?.action_description?.body?.priority,

            not_linked_to_case: matchedExecution?.action_description?.body?.not_linked_to_case,

            due_date: matchedExecution?.action_description?.body?.due_date,

            due_date_count: matchedExecution?.action_description?.body?.due_date_count,
            due_date_period: matchedExecution?.action_description?.body?.due_date_period,
            due_date_cycle: matchedExecution?.action_description?.body?.due_date_cycle || 0,

            reminder: payloadCompatibleReminder,

            description: matchedExecution?.action_description?.body?.description,

            has_subtask: matchedExecution?.action_description?.body?.sub_task > 0,
            sub_task: matchedExecution?.action_description?.body?.sub_task,

            selectedAssignees: functionParams?.selectedFirmUsers,
            selectedAssigneeDetailsObjectArr: assigneeDetailsObjectArr,
            selectedReminderObjectArr: matchedExecution?.action_description?.body?.selectedReminderObjectArr,
            track_action_time: matchedExecution?.action_description?.body?.track_action_time,
            manual_total_hours: matchedExecution?.action_description?.body?.manual_total_hours,
            action_unique_identifier: matchedExecution?.action_description?.body?.action_unique_identifier,
          },
        };

        // Update the matched execution in the selectedAutomationRuleDetails
        functionParams.selectedAutomationRuleDetails.workflow.execution_sequence[matchedIndex] = {
          ...matchedExecution,
          action_description: {
            ...matchedExecution.action_description,
            body: request_object, // Update with the new request_object
          },
        };

        try {
          let dataToBeSent = {
            ...ruleValidationCheckPayload,
            automation_rule_id: functionParams?.selectedAutomationRuleDetails?.automation_rule_id,
            is_deleted: functionParams?.selectedAutomationRuleDetails?.is_deleted,
            is_error: functionParams?.selectedAutomationRuleDetails?.is_error,
            res_type: functionParams?.selectedAutomationRuleDetails?.res_type,
            searchables: functionParams?.selectedAutomationRuleDetails?.searchables,
            tenant_id: functionParams?.selectedAutomationRuleDetails?.tenant_id,
            time: moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + '000+00:00',
            type_tenant: functionParams?.selectedAutomationRuleDetails?.tenant_id,
            user_id: functionParams?.selectedAutomationRuleDetails?.user_id,
            workflow_title: functionParams?.selectedAutomationRuleDetails?.workflow_title,
            automation_status: functionParams?.selectedAutomationRuleDetails?.automation_status?.toLocaleUpperCase(),
            is_lead_automation: functionParams?.selectedAutomationErrorLogDetails?.is_lead,
            // validation_trigger_code: triggerName?.ruleValidationCode,
            // validation_action_code: generateAutomationRuleValidationActionCodesList(globalAutomationData),
            workflow_description: functionParams?.selectedAutomationRuleDetails?.workflow_description,
            workflow: {
              fire_on: {
                trigger_code: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.trigger_code,
                module: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.module,
                label: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.label,
              },
              terminate_on: {},
              // filter: filter?.length > 0 && filter[0]?.filterBody?.label ? filter[0]?.filterBody : {},
              filter: functionParams?.selectedAutomationRuleDetails?.filter || [],
              action_period_expression: functionParams?.selectedAutomationRuleDetails?.workflow?.action_period_expression,
              execution_sequence: [...functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence],
            },
            practice_area: functionParams?.selectedAutomationRuleDetails?.practice_area,
          };
          dataToBeSent = {
            ...dataToBeSent,
            pk: functionParams?.selectedAutomationRuleDetails?.pk,
            sk: functionParams?.selectedAutomationRuleDetails?.sk,
            error_pk: errorPk,
            error_sk: errorSk,
          };

          let response = await updateWorkFlowAutomationRules(true, dataToBeSent);

          if (response) {
            functionParams?.onErrorFixSuccess();
          }
        } catch (error) {
          let errMsg = error?.response?.data;
          functionParams?.onErrorFixFail(errMsg);
          console.log('error', error);
        }
      }
    } catch (error) {
      console.log('error', error);
    }
  } else if (functionParams?.selectedAutomationErrorLogDetails?.action_code === 'ACT_SEND_SECURE_MESSAGE') {
    try {
      // Extract the array of action unique identifiers from the execution sequence
      const actionUniqueIdentifiers = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence?.map(
        (action) => action.action_unique_identifier
      );

      // Check if the action_unique_identifier exists in the extracted array
      const isIdentifierMatched = actionUniqueIdentifiers?.includes(
        functionParams?.selectedAutomationErrorLogDetails?.action_unique_identifier
      );

      // Declare matchedExecution and matchedIndex outside of the if statement
      let matchedExecution;
      let matchedIndex = -1; // Default value if no match is found

      if (isIdentifierMatched) {
        // Find the matching execution details and index
        matchedIndex = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence.findIndex(
          (action) => action.action_unique_identifier === functionParams?.selectedAutomationErrorLogDetails?.action_unique_identifier
        );

        // Get the matched execution using the found index
        matchedExecution = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence[matchedIndex];

        // Implement the mapping and filtering logic based on the provided conditions
        const newSelectedFirmUsers = matchedExecution?.action_description?.body?.data?.body?.selectedMessageRecipientDetailsObjectArr
          ?.map((option) => {
            if (option.res_type === 'user') {
              // Check if the user is confirmed and active
              if (option.is_confirmed === true && option.status === 'ACTIVE') {
                return option?.client_cognito_username;
              }
            } else if (option?.res_type === 'user-group') {
              // Check if the group status is active
              if (option?.status === 'ACTIVE' || option?.status === undefined) {
                return option?.group_id;
              }
            } else {
              // If res_type is not present, take the value directly
              return option?.value;
            }
            return null; // Return null if none of the conditions match
          })
          .filter(Boolean); // Filter out any null or undefined values

        // Combine newSelectedFirmUsers with the existing selectedFirmUsers
        functionParams.selectedFirmUsers = [
          ...functionParams.selectedFirmUsers, // Existing selected users
          ...newSelectedFirmUsers, // Newly filtered users
        ];

        // Remove duplicates if necessary
        functionParams.selectedFirmUsers = Array.from(new Set(functionParams.selectedFirmUsers));

        // Continue with the remaining logic

        let contactAssigneesList = [];
        let teamMemberRoles = [];
        let firmUserRecipientsList = [];
        let userGroupsList = [];
        let accessibleList = [];
        let obj = {};
        let request_object = {};

        const newGroup = {
          groupLabel: 'Team Member Roles',
          code: 'TeamMemberRoles',
          items: [...teamMemberRolesList],
        };

        // Insert the new group at the second position
        const updatedOptions = [
          functionParams?.fullAttendeesList[0], // First existing group
          newGroup, // Your new group
          ...functionParams?.fullAttendeesList.slice(1), // Remaining existing groups
        ];

        // Create a set to track unique group labels
        const uniqueGroupLabels = new Set();

        // Filter out duplicate groups based on groupLabel
        const uniqueUpdatedOptions = updatedOptions?.filter((option) => {
          if (!uniqueGroupLabels?.has(option?.groupLabel)) {
            uniqueGroupLabels?.add(option?.groupLabel);
            return true;
          }
          return false;
        });

        let messageRecipientDetailsObjectArr = generateMessageRecipientsDetailsObjectArray({
          messageRecipientsList: uniqueUpdatedOptions,
          messageToWatched: functionParams.selectedFirmUsers,
        });

        //filtering all assingnees other than billing and primary
        //filtering all assingnees other than billing and primary
        firmUserRecipientsList = generatePayloadCompatibleFormattedMessageRecipientsList({
          messageRecipientsArr: messageRecipientDetailsObjectArr,
          isFirmUser: true,
          isBillingOrPrimary: false,
          isUserGroup: false,
          isTeamMember: false,
        });

        //filtering primary and billing contacts
        contactAssigneesList = generatePayloadCompatibleFormattedMessageRecipientsList({
          messageRecipientsArr: messageRecipientDetailsObjectArr,
          isBillingOrPrimary: true,
          isFirmUser: false,
          isUserGroup: false,
          isTeamMember: false,
        });

        //filtering only user-groups list
        userGroupsList = generatePayloadCompatibleFormattedMessageRecipientsList({
          messageRecipientsArr: messageRecipientDetailsObjectArr,
          isUserGroup: true,
          isBillingOrPrimary: false,
          isFirmUser: false,
          isTeamMember: false,
        });

        // filtering team member roles
        teamMemberRoles = generatePayloadCompatibleFormattedMessageRecipientsList({
          messageRecipientsArr: messageRecipientDetailsObjectArr,
          isTeamMember: true,
          isUserGroup: false,
          isBillingOrPrimary: false,
          isFirmUser: false,
        });

        accessibleList = getAccessibleList(firmUserRecipientsList);

        const ruleValidationCheckPayload = {
          validation_trigger_code: functionParams?.selectedAutomationRuleDetails?.validation_trigger_code,
          validation_action_code: functionParams?.selectedAutomationRuleDetails?.validation_action_code,
        };

        request_object = {
          ...obj,
          ...{
            // action: 'message',
            // type: 'secure_message',
            action_unique_identifier: matchedExecution?.action_description?.body?.action_unique_identifier,

            // secure_message_type: 'thread',
            tenant_id: matchedExecution?.action_description?.body?.data?.tenantId,
            subject: matchedExecution?.action_description?.body?.data?.body?.subject,
            // sendData.data.body.message = message?.htmlValue;
            message: matchedExecution?.action_description?.body?.data?.body?.message,
            contact_assignees: contactAssigneesList,
            firm_user_assignees: teamMemberRoles,
            message_to: firmUserRecipientsList,
            accessible_list: accessibleList,
            groups: userGroupsList,

            selectedMessageSender: matchedExecution?.action_description?.body?.data?.body?.selectedMessageSender,
            selectedMessageRecipients: functionParams?.selectedFirmUsers,
            selectedMessageRecipientDetailsObjectArr: messageRecipientDetailsObjectArr,

            attachment: matchedExecution?.action_description?.body?.attachment || [],
            track_action_time: matchedExecution?.action_description?.body?.data?.body?.track_action_time,
            manual_total_hours: matchedExecution?.action_description?.body?.data?.body?.manual_total_hours,
          },
        };

        // Update the request_object to the specific nested location
        functionParams.selectedAutomationRuleDetails.workflow.execution_sequence[matchedIndex] = {
          ...matchedExecution,
          action_description: {
            ...matchedExecution.action_description,
            body: {
              ...matchedExecution.action_description.body,
              data: {
                ...matchedExecution.action_description.body.data,
                body: {
                  ...matchedExecution.action_description.body.data.body,
                  ...request_object, // This spreads the new request_object values inside the existing structure
                },
              },
            },
          },
        };

        try {
          let dataToBeSent = {
            ...ruleValidationCheckPayload,
            automation_rule_id: functionParams?.selectedAutomationRuleDetails?.automation_rule_id,
            is_deleted: functionParams?.selectedAutomationRuleDetails?.is_deleted,
            is_error: functionParams?.selectedAutomationRuleDetails?.is_error,
            res_type: functionParams?.selectedAutomationRuleDetails?.res_type,
            searchables: functionParams?.selectedAutomationRuleDetails?.searchables,
            tenant_id: functionParams?.selectedAutomationRuleDetails?.tenant_id,
            time: moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + '000+00:00',
            type_tenant: functionParams?.selectedAutomationRuleDetails?.tenant_id,
            user_id: functionParams?.selectedAutomationRuleDetails?.user_id,
            workflow_title: functionParams?.selectedAutomationRuleDetails?.workflow_title,
            automation_status: functionParams?.selectedAutomationRuleDetails?.automation_status?.toLocaleUpperCase(),
            is_lead_automation: functionParams?.selectedAutomationErrorLogDetails?.is_lead,
            // validation_trigger_code: triggerName?.ruleValidationCode,
            // validation_action_code: generateAutomationRuleValidationActionCodesList(globalAutomationData),
            workflow_description: functionParams?.selectedAutomationRuleDetails?.workflow_description,
            workflow: {
              fire_on: {
                trigger_code: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.trigger_code,
                module: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.module,
                label: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.label,
              },
              terminate_on: {},
              // filter: filter?.length > 0 && filter[0]?.filterBody?.label ? filter[0]?.filterBody : {},
              filter: functionParams?.selectedAutomationRuleDetails?.filter || [],
              action_period_expression: functionParams?.selectedAutomationRuleDetails?.workflow?.action_period_expression,
              execution_sequence: [...functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence],
            },
            practice_area: functionParams?.selectedAutomationRuleDetails?.practice_area,
          };
          dataToBeSent = {
            ...dataToBeSent,
            pk: functionParams?.selectedAutomationRuleDetails?.pk,
            sk: functionParams?.selectedAutomationRuleDetails?.sk,
            error_pk: errorPk,
            error_sk: errorSk,
          };

          let response = await updateWorkFlowAutomationRules(true, dataToBeSent);

          if (response) {
            functionParams?.onErrorFixSuccess();
          }
        } catch (error) {
          let errMsg = error?.response?.data;
          functionParams?.onErrorFixFail(errMsg);
          console.log('error', error);
        }
      }
    } catch (error) {
      console.log('error', error);
    }
  }
};

const handleSenderAutomationErrors = async (functionParams) => {
  try {
    // Extract the array of action unique identifiers from the execution sequence
    const actionUniqueIdentifiers = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence?.map(
      (action) => action.action_unique_identifier
    );

    // Check if the action_unique_identifier exists in the extracted array
    const isIdentifierMatched = actionUniqueIdentifiers?.includes(
      functionParams?.selectedAutomationErrorLogDetails?.action_unique_identifier
    );

    // Declare matchedExecution and matchedIndex outside of the if statement
    let matchedExecution;
    let matchedIndex = -1; // Default value if no match is found

    if (isIdentifierMatched) {
      //   // Find the matching execution details and index
      matchedIndex = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence.findIndex(
        (action) => action.action_unique_identifier === functionParams?.selectedAutomationErrorLogDetails?.action_unique_identifier
      );

      //   // Get the matched execution using the found index
      matchedExecution = functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence[matchedIndex];

      const teamMembersRoleValuesList = ['lead_attorney', 'lead_associate', 'lead_paralegal', 'lead_case_manager'];
      // Update the creator-related fields
      // Ensure matchedExecution and necessary properties exist
      if (matchedExecution) {
        // Update the creator-related fields

        let updatedBody = {};
        let updatedDataBody = {};
        const bodyData = matchedExecution.action_description.body.data.body;
        if (teamMembersRoleValuesList.includes(functionParams?.selectedSender)) {
          updatedBody = {
            ...bodyData, // Spread existing body data to keep all other properties
            message_sender_assignee: functionParams?.selectedSender, // Update the specific property
            selectedMessageSender: functionParams?.selectedSender,
          };
          delete bodyData?.status;
          delete bodyData?.is_confirmed;
          delete matchedExecution.action_description.body?.creator_first_name;
          delete matchedExecution.action_description.body?.creator_middle_name;
          delete matchedExecution.action_description.body?.creator_last_name;
          delete matchedExecution.action_description.body?.creator_email;
          delete matchedExecution.action_description.body?.creator_user_id;
          delete matchedExecution.action_description.body?.creator_firm_user_id;
          delete matchedExecution.action_description.body?.creator_access_level;
        } else {
          const messageSenderDetailsObj = getMessageSenderDetails({
            messageRecipientsList: functionParams?.messageRecipientsList,
            messageFrom: functionParams?.selectedSender,
          });

          updatedDataBody = {
            creator_first_name: messageSenderDetailsObj.first_name || '',
            creator_middle_name: messageSenderDetailsObj.middle_name || '',
            creator_last_name: messageSenderDetailsObj.last_name || '',
            creator_email: messageSenderDetailsObj.email || '',
            creator_user_id: messageSenderDetailsObj.client_cognito_username || '',
            creator_firm_user_id: messageSenderDetailsObj.firm_user_id || '',
            creator_access_level: messageSenderDetailsObj.access_level || null,
          };
          matchedExecution.action_description.body.data.body.selectedMessageSender = functionParams?.selectedSender;
          delete matchedExecution.action_description.body?.data?.body?.message_sender_assignee;
          delete matchedExecution.action_description.body?.status;
          delete matchedExecution.action_description.body?.is_confirmed;
        }
        // Update the selectedAutomationRuleDetails structure with the new values
        functionParams.selectedAutomationRuleDetails.workflow.execution_sequence[matchedIndex] = {
          ...matchedExecution,
          action_description: {
            ...matchedExecution.action_description,
            body: {
              ...matchedExecution.action_description.body,
              ...updatedDataBody,
              data: {
                ...matchedExecution.action_description.body.data,
                body: {
                  ...matchedExecution.action_description.body.data?.body,
                  ...updatedBody,
                },
              },
            },
          },
        };

        try {
          const ruleValidationCheckPayload = {
            validation_trigger_code: functionParams?.selectedAutomationRuleDetails?.validation_trigger_code,
            validation_action_code: functionParams?.selectedAutomationRuleDetails?.validation_action_code,
          };

          let dataToBeSent = {
            ...ruleValidationCheckPayload,
            automation_rule_id: functionParams?.selectedAutomationRuleDetails?.automation_rule_id,
            is_deleted: functionParams?.selectedAutomationRuleDetails?.is_deleted,
            is_error: functionParams?.selectedAutomationRuleDetails?.is_error,
            res_type: functionParams?.selectedAutomationRuleDetails?.res_type,
            searchables: functionParams?.selectedAutomationRuleDetails?.searchables,
            tenant_id: functionParams?.selectedAutomationRuleDetails?.tenant_id,
            time: moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS') + '+00:00',
            type_tenant: functionParams?.selectedAutomationRuleDetails?.tenant_id,
            user_id: functionParams?.selectedAutomationRuleDetails?.user_id,
            workflow_title: functionParams?.selectedAutomationRuleDetails?.workflow_title,
            automation_status: functionParams?.selectedAutomationRuleDetails?.automation_status?.toLocaleUpperCase(),
            is_lead_automation: functionParams?.selectedAutomationErrorLogDetails?.is_lead,
            // validation_trigger_code: triggerName?.ruleValidationCode,
            // validation_action_code: generateAutomationRuleValidationActionCodesList(globalAutomationData),
            workflow_description: functionParams?.selectedAutomationRuleDetails?.workflow_description,
            workflow: {
              fire_on: {
                trigger_code: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.trigger_code,
                module: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.module,
                label: functionParams?.selectedAutomationErrorLogDetails?.fire_on?.label,
              },
              terminate_on: {},
              // filter: filter?.length > 0 && filter[0]?.filterBody?.label ? filter[0]?.filterBody : {},
              filter: functionParams?.selectedAutomationRuleDetails?.filter || [],
              action_period_expression: functionParams?.selectedAutomationRuleDetails?.workflow?.action_period_expression,
              execution_sequence: [...functionParams?.selectedAutomationRuleDetails?.workflow?.execution_sequence],
            },
            practice_area: functionParams?.selectedAutomationRuleDetails?.practice_area,
          };
          dataToBeSent = {
            ...dataToBeSent,
            pk: functionParams?.selectedAutomationRuleDetails?.pk,
            sk: functionParams?.selectedAutomationRuleDetails?.sk,
            error_pk: functionParams?.errorPk,
            error_sk: functionParams?.errorSk,
          };

          let response = await updateWorkFlowAutomationRules(true, dataToBeSent);

          if (response) {
            functionParams?.onErrorFixSuccess();
          }
        } catch (error) {
          let errMsg = error?.response?.data;
          functionParams?.onErrorFixFail(errMsg);
          console.log('error', error);
        }
      }
    }
  } catch (error) {
    console.log('error', error);
  }
};

const handleBusinessEmailAutomationErrors = async (functionParams) => {
  try {
    let responseOne = await getUserDetails('', '', functionParams?.firm_user_id, '');
    if (responseOne?.data) {
      let data = {
        ...responseOne?.data,
        business_email: functionParams?.businessEmail,
      };
      let responseTwo = await updateUserDetails(data);
      if (responseTwo?.data) {
        functionParams?.onErrorFixSuccess();
      }
    }
  } catch (error) {
    let errMsg = error?.response?.data;
    functionParams?.onErrorFixFail(errMsg);
    console.log('error', error);
  }
};

const handleLeadUserAutomationErrors = async (params = {}) => {
  const { is_lead, case_id, selectedTeamMember, onErrorFixSuccess, onErrorFixFail } = params;

  try {
    if (selectedTeamMember) {
      let response = await addTeamMember({ team_member: [selectedTeamMember], old_team_member: null }, '', case_id, is_lead);

      if (response?.data) onErrorFixSuccess();
    }
  } catch (error) {
    let errMsg = error?.response?.data;
    onErrorFixFail(errMsg);
    console.log('error', error);
  }
};

const handleLawftIdAutomationErrors = async (params = {}) => {
  const { is_lead, case_id, lawft_id, onErrorFixSuccess, onErrorFixFail, contactData } = params;
  try {
    let data = contactData || {};
    data.lawft_id = Array.isArray(data?.lawft_id) ? [lawft_id, ...data?.lawft_id] : [lawft_id];
    if (contactData?.is_client) {
      let responseTwo = await updateCaseContact(case_id, contactData?.contact_id, data, true, is_lead);
      if (responseTwo?.data) {
        onErrorFixSuccess();
      }
    } else {
      const payload = {
        relations: [data],
      };
      let responseTwo = await updateRelation(payload, is_lead, null);
      if (responseTwo?.data) {
        onErrorFixSuccess();
      }
    }
  } catch (error) {
    let errMsg = error?.response?.data;
    onErrorFixFail(errMsg);
    console.log('error', error);
  }
};

//========================== Error fix handling functsions ends ================================================

const automationErrorLogApiServices = {
  getCasesAndLeads,
  getBillingContactsList,
  getContactDetails,
  getTeamMembersList,
  handleUserNotInCaseError,
  handleUpdateErrorOccuredContactDetails,
  handleNoBillingContactError,
  handleNoLeadTeamMemberError,
  handleUsersAutomationErrors,
  handleSenderAutomationErrors,
  handleBusinessEmailAutomationErrors,
  handleLeadUserAutomationErrors,
  handleLawftIdAutomationErrors,
};

export default automationErrorLogApiServices;
